import AIChatWidget from '@/components/aichat/AiChatWidget';
import SeoHead from '@/components/seo/SeoHead';
import LandingLayout from '@/layouts/landing-layout';
import { Link } from '@inertiajs/react';
import { ArrowRight, ChevronRight } from 'lucide-react';

const baseUrl = (import.meta as any).env?.VITE_APP_URL ?? (typeof window !== 'undefined' ? window.location.origin : '');

type SdgSummary = {
    id: number;
    sdg_number: number;
    title: string;
    short_title: string;
    description: string;
    icon?: string | null;
    color?: string | null;
    image?: string | null;
    slug: string;
    counts: {
        news: number;
        events: number;
        researches: number;
        activities: number;
        reports: number;
        total: number;
    };
};

type Props = {
    sdgs: SdgSummary[];
    latestReports: Array<{
        id: number;
        title: string;
        report_type: string;
        report_year: number | null;
        summary: string | null;
        published_at: string | null;
        url: string | null;
        image: string | null;
        sdgs: Array<{
            sdg_number: number;
            short_title: string;
            slug: string;
            image?: string | null;
        }>;
    }>;
};

function formatDate(value: string | null) {
    if (!value) {
        return null;
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return value;
    }

    return parsed.toLocaleDateString('en-PH', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
}

export default function SustainableDevelopmentGoals({ sdgs, latestReports }: Props) {
    return (
        <LandingLayout>
            <SeoHead
                title="Sustainable Development Goals"
                description="Explore Northeastern College's SDG-aligned research, news, events, and institutional activities."
                image={`${baseUrl}/images/logo.webp`}
            />

            <section className="bg-muted pb-16">
                <div className="relative min-h-[380px] overflow-hidden bg-black md:min-h-[460px]">
                    <img
                        src="/images/hero/sdg.png"
                        alt="Northeastern College SDG initiatives"
                        className="absolute inset-0 h-full w-full scale-105 object-cover opacity-60"
                    />

                    <div className="absolute inset-0 bg-gradient-to-r from-[#02301c]/95 via-[#02301c]/80 to-transparent" />
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(10,122,53,0.35),transparent_60%)]" />
                    <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:3rem_3rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" />

                    <div className="relative z-10 mx-auto flex min-h-[380px] max-w-7xl flex-col justify-center px-4 py-16 text-white md:min-h-[460px] md:py-24">
                        <div className="mb-6 flex items-center gap-2 text-xs font-semibold tracking-wide text-[#21d36b]/90 uppercase md:text-sm">
                            <Link href="/" prefetch className="transition-colors hover:text-emerald-300">
                                Home
                            </Link>
                            <ChevronRight className="size-4" aria-hidden="true" />
                            <span className="text-white/80">Sustainable Development Goals</span>
                        </div>

                        <h1 className="max-w-5xl text-4xl leading-tight font-extrabold tracking-tight text-white md:text-6xl">
                            Sustainable Development Goals
                        </h1>
                        <p className="mt-6 max-w-3xl text-base leading-relaxed font-normal text-white/85 md:text-lg">
                            Northeastern College&apos;s SDG page now works as a live aggregation hub, bringing together tagged research, news, events,
                            and institutional activities under the Sustainable Development Goals.
                        </p>

                    </div>
                </div>

                <div className="relative z-20 mx-auto -mt-8 max-w-7xl px-4">
                    <div className="bg-card border-border mb-10 overflow-hidden rounded-2xl border shadow-sm">
                        <div className="p-6 md:p-8">
                            <div className="text-muted-foreground max-w-5xl space-y-4 text-sm leading-relaxed md:text-base">
                                <p>
                                    Northeastern College is committed to supporting the achievement of a more sustainable and inclusive future through
                                    the 17 Sustainable Development Goals. As an educational institution in Santiago City, NC continues to align its
                                    research, community activities, academic initiatives, and institutional programs with development goals that
                                    strengthen people, communities, and the environment.
                                </p>
                                <p>
                                    Through SDG-aligned learning, outreach, and partnerships, the College aims to contribute meaningful local action
                                    toward national and global progress. This page brings together the goals and the related NC content connected to
                                    them, helping present a clearer picture of the institution&apos;s ongoing commitment to sustainable development.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border-border mb-10 overflow-hidden rounded-2xl border shadow-sm">
                        <div className="p-6 md:p-8">
                            <h2 className="text-foreground text-2xl font-bold tracking-tight md:text-3xl">All 17 Sustainable Development Goals</h2>
                            <p className="text-muted-foreground mt-2 max-w-3xl text-sm leading-relaxed md:text-base">
                                Explore all 17 Sustainable Development Goals and open each goal to view related Northeastern College research, news,
                                events, activities, and reports.
                            </p>
                        </div>

                        <div className="border-border grid grid-cols-2 gap-4 border-t p-6 sm:grid-cols-3 md:p-8 lg:grid-cols-6">
                            {sdgs.map((sdg) => (
                                <Link
                                    key={sdg.id}
                                    href={route('sdg.show', { sdg: sdg.slug })}
                                    prefetch
                                    className="group bg-card border-border overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/30 hover:shadow-md"
                                >
                                    {sdg.image ? (
                                        <img
                                            src={sdg.image}
                                            alt={`SDG ${sdg.sdg_number}: ${sdg.short_title}`}
                                            className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div
                                            className="flex aspect-square w-full items-center justify-center p-6 text-center text-lg font-bold text-white"
                                            style={{ backgroundColor: sdg.color ?? '#0A7A35' }}
                                        >
                                            SDG {sdg.sdg_number}
                                        </div>
                                    )}
                                </Link>
                            ))}

                            <div className="bg-card border-border overflow-hidden rounded-2xl border shadow-sm">
                                <img
                                    src="/images/sdg.png"
                                    alt="Sustainable Development Goals"
                                    className="aspect-square w-full object-cover"
                                    loading="lazy"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="bg-card border-border overflow-hidden rounded-2xl border shadow-sm">
                        <div className="p-6 md:p-8">
                            <h2 className="text-foreground text-2xl font-bold tracking-tight md:text-3xl">Annual SDG Reports</h2>
                            <p className="text-muted-foreground mt-2 max-w-3xl text-sm leading-relaxed md:text-base">
                                Formal SDG and sustainability documents now sit beside live tagged content, giving the hub both evidence and reporting
                                value.
                            </p>
                        </div>

                        {latestReports.length === 0 ? (
                            <div className="border-border border-t px-6 py-12 text-center">
                                <p className="text-muted-foreground text-sm md:text-base">No SDG reports available yet.</p>
                            </div>
                        ) : (
                            <div className="border-border grid gap-6 border-t p-6 md:grid-cols-2 md:p-8 xl:grid-cols-3">
                                {latestReports.map((report) => (
                                    <div
                                        key={report.id}
                                        className="bg-card border-border flex h-full flex-col overflow-hidden rounded-2xl border shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-emerald-500/20 hover:shadow-md"
                                    >
                                        {report.image && <img src={report.image} alt={report.title} className="h-44 w-full object-cover" />}

                                        <div className="flex flex-1 flex-col p-5">
                                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                                <span className="rounded-full bg-[#0A7A35]/10 px-3 py-1 text-[11px] font-bold tracking-wider text-[#0A7A35] uppercase">
                                                    {report.report_type}
                                                </span>
                                                {(report.published_at || report.report_year) && (
                                                    <span className="text-muted-foreground text-xs">
                                                        {formatDate(report.published_at) ?? report.report_year}
                                                    </span>
                                                )}
                                            </div>

                                            <h3 className="text-foreground line-clamp-2 text-base font-bold">{report.title}</h3>
                                            {report.summary && <p className="text-muted-foreground mt-3 line-clamp-3 text-sm leading-relaxed">{report.summary}</p>}

                                            <div className="mt-4 flex flex-wrap gap-2">
                                                {report.sdgs.map((sdg) => (
                                                    <Link
                                                        key={`${report.id}-${sdg.slug}`}
                                                        href={route('sdg.show', { sdg: sdg.slug })}
                                                        prefetch
                                                        className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-1 text-[11px] font-semibold text-muted-foreground"
                                                    >
                                                        {sdg.image ? (
                                                            <img
                                                                src={sdg.image}
                                                                alt={`SDG ${sdg.sdg_number}`}
                                                                className="h-4 w-4 rounded-sm object-cover"
                                                            />
                                                        ) : null}
                                                        SDG {sdg.sdg_number}
                                                    </Link>
                                                ))}
                                            </div>

                                            {report.url && (
                                                <a
                                                    href={report.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-[#0A7A35] hover:underline"
                                                >
                                                    Open report
                                                    <ArrowRight className="size-4" />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                </div>
            </section>

            <AIChatWidget />
        </LandingLayout>
    );
}
